import React, { CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Text from '@embracesbs/component-text'
import Autocomplete from '@embracesbs/component-autocomplete'
import Button2 from '@embracesbs/component-button2/Button2'
import Toast, { customToast } from '@embracesbs/component-toast'
import { useTenants } from '../../Context/TenantContext'
import { useApi, useAuth } from '../../../Hooks'
import { AuthRoleContext } from '../../Auth/AuthRoleContext'
import LoadingState from '../../Common/LoadingState'
import { CertificateIssuer, TenantCertificates as TenantCertificatesDto, TenantIngressApplyResult } from '../../../Interfaces/Types'

// Not '': the Autocomplete treats an empty value as unset and picks an option itself.
const DEFAULT_VALUE = '__default__'
const APP_FONT = "'F-Default', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

const monoStyle: CSSProperties = { fontFamily: MONO_FONT, fontSize: '12px', color: 'var(--tma-text-muted)', wordBreak: 'break-all' }

// Status tokens so the accessibility themes can substitute hues (index.css).
const pill = (color: string): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  color,
  background: `color-mix(in srgb, ${color} 12%, transparent)`,
  borderRadius: '999px',
  padding: '2px 8px',
  fontSize: '12px',
  fontWeight: 600,
  whiteSpace: 'nowrap'
})

const OUTCOME_TEXT: Record<TenantIngressApplyResult['outcome'], string> = {
  Applied: 'switched in the cluster',
  Unchanged: 'already on that issuer',
  NotDeployed: 'saved; the next deploy creates the ingress with it',
  Disabled: 'saved; cluster sync is off here',
  Failed: 'saved, but the cluster refused the change'
}

const apiError = (error: any, fallback: string): string => {
  const message = error?.response?.data ?? error?.data
  return typeof message === 'string' && message.trim() && message.length <= 400 ? message : fallback
}

// Typing in the issuer field matches the display name, the ClusterIssuer name and the
// description, so a longer catalog stays easy to pick from.
const matchesIssuer = (option: { label: string; value: string; data: any }, input: string): boolean => {
  const q = input.trim().toLowerCase()
  return !q || [option.label, option.value, option.data?.description ?? ''].some((v) => String(v).toLowerCase().includes(q))
}

// Which certificate issuer the tenant's ingresses use. Saving stores the choice and
// switches the live ingress right away (cert-manager then re-issues the certificate);
// the deploy reads the same choice. Unlike the Deploytime form this has its own Save.
const TenantCertificates: React.FC = () => {
  const { fetchApi } = useApi()
  const { isLoggedIn } = useAuth()
  const { selectedTenantId } = useTenants()
  const roles = useContext(AuthRoleContext)
  const canEdit = !!roles?.isWriter || !!roles?.isAdmin

  const [data, setData] = useState<TenantCertificatesDto | null>(null)
  const [issuers, setIssuers] = useState<CertificateIssuer[]>([])
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const applyData = (next: TenantCertificatesDto) => {
    setData(next)
    setDraft(Object.fromEntries(next.ingresses.map((i) => [i.key, i.selectedIssuer ?? DEFAULT_VALUE])))
  }

  const load = useCallback(async () => {
    if (!isLoggedIn || !selectedTenantId) return
    try {
      const [certificates, catalog] = await Promise.all([
        fetchApi({ endpoint: `/api/tenant/${selectedTenantId}/certificates`, method: 'GET' }),
        fetchApi({ endpoint: '/api/certificate-issuers', method: 'GET' })
      ])
      if (!certificates) return
      applyData(certificates)
      setIssuers(Array.isArray(catalog) ? catalog : [])
      setLoadError(null)
    } catch (error: any) {
      setLoadError(apiError(error, 'The certificate settings could not be loaded.'))
    }
  }, [fetchApi, isLoggedIn, selectedTenantId])

  useEffect(() => {
    setData(null)
    load()
  }, [load])

  const options = useMemo(() => {
    const known = [...issuers]
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .map((i) => ({
        label: `${i.displayName} (${i.name})${i.clusterState === 'Ready' || i.clusterState === 'Disabled' ? '' : ' · not ready'}`,
        value: i.name,
        description: i.description ?? ''
      }))
    // A stored issuer that has left the catalog stays visible instead of looking like the default.
    const stored = (data?.ingresses ?? []).map((i) => i.selectedIssuer).filter((n): n is string => !!n)
    const unknown = Array.from(new Set(stored))
      .filter((name) => !issuers.some((i) => i.name === name))
      .map((name) => ({ label: `${name} (not in catalog)`, value: name }))
    return [{ label: `Default (${data?.defaultIssuer ?? 'letsencrypt-prod'})`, value: DEFAULT_VALUE }, ...known, ...unknown]
  }, [issuers, data])

  const isDirty = !!data && data.ingresses.some((i) => (draft[i.key] ?? DEFAULT_VALUE) !== (i.selectedIssuer ?? DEFAULT_VALUE))
  const blockedReason = !data
    ? null
    : data.deployRunning
      ? 'A deploy is running for this tenant. Change the issuer once it has finished.'
      : !data.servesEnvironment
        ? `This tenant's stage isn't on ${data.clusterName || "this TMA's cluster"}.`
        : null

  const save = async () => {
    if (!data || !isDirty) return
    setIsSaving(true)
    try {
      const issuersByKey = Object.fromEntries(
        data.ingresses.map((i) => [i.key, draft[i.key] && draft[i.key] !== DEFAULT_VALUE ? draft[i.key] : null])
      )
      const response = await fetchApi({
        endpoint: `/api/tenant/${selectedTenantId}/certificates`,
        method: 'PUT',
        body: { issuers: issuersByKey }
      })
      if (response?.certificates) applyData(response.certificates)
      const results: TenantIngressApplyResult[] = response?.results ?? []
      const labelOf = (key: string) => data.ingresses.find((i) => i.key === key)?.label ?? key
      const changed = results.filter((r) => r.outcome !== 'Unchanged')
      const failed = changed.filter((r) => r.outcome === 'Failed')
      const summary = changed.map((r) => `${labelOf(r.key)}: ${OUTCOME_TEXT[r.outcome]}`).join(' · ')
      if (failed.length > 0) customToast.error(`${summary}. ${failed.map((r) => r.message).join(' ')}`)
      else customToast.success(summary || 'Saved')
    } catch (error: any) {
      customToast.error(apiError(error, 'The certificate settings could not be saved.'))
    } finally {
      setIsSaving(false)
    }
  }

  const renderLive = (ingress: TenantCertificatesDto['ingresses'][number]) => {
    switch (ingress.liveState) {
      case 'Disabled':
        return <span style={pill('var(--tma-text-muted)')}>Cluster sync off</span>
      case 'Missing':
        return (
          <span style={pill('var(--tma-text-muted)')} data-tooltip-id='global-tooltip' data-tooltip-content={ingress.message ?? undefined}>
            Not deployed yet
          </span>
        )
      case 'Unknown':
        return (
          <span style={pill('var(--tma-text-muted)')} data-tooltip-id='global-tooltip' data-tooltip-content={ingress.message ?? undefined}>
            Unknown
          </span>
        )
      default: {
        // The annotation changes at once; the certificate follows when cert-manager has
        // issued it from the new issuer.
        const switching = !!ingress.certificateIssuer && ingress.certificateIssuer !== ingress.liveIssuer
        const issued = ingress.liveState === 'Ready' && !switching
        return (
          <span
            style={pill(issued ? 'var(--tma-status-ok-strong)' : 'var(--tma-warning-text)')}
            data-tooltip-id='global-tooltip'
            data-tooltip-content={ingress.message ?? undefined}
          >
            {issued ? `Issued by ${ingress.liveIssuer}` : `Issuing from ${ingress.liveIssuer ?? 'unknown issuer'}…`}
          </span>
        )
      }
    }
  }

  if (loadError) return <Text textStyle='description' style={{ color: 'var(--tma-danger-text)' }} content={loadError} />
  if (!data) return <LoadingState text='Loading certificate settings…' />
  if (data.ingresses.length === 0)
    return <Text textStyle='description' textColor='grey' content='This tenant has no Suite hostname or Portal UI ingress yet.' />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%', fontFamily: APP_FONT }}>
      <Toast />
      <Text
        textStyle='description'
        textColor='grey'
        content={
          data.clusterEnabled
            ? `Saving switches the ingress in ${data.clusterName || 'the cluster'} right away; cert-manager then issues a new certificate from that issuer. Deploys keep the choice.`
            : 'Cluster sync is off in this environment: saving only stores the choice.'
        }
      />
      {blockedReason && <Text textStyle='description' style={{ color: 'var(--tma-warning-text)' }} content={blockedReason} />}
      {issuers.length === 0 && (
        <Text textStyle='description' textColor='grey'>
          No issuers are in the catalog yet. An administrator can add them on the{' '}
          <Link to='/certificates' style={{ color: 'var(--tma-accent)' }}>
            Certificates
          </Link>{' '}
          page.
        </Text>
      )}
      {data.ingresses.map((ingress) => (
        <div key={ingress.key} style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: 3, minWidth: '240px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, color: 'var(--tma-text-strong)' }}>{ingress.label}</span>
              {renderLive(ingress)}
            </div>
            <div style={{ ...monoStyle, marginTop: '4px' }}>{ingress.hostnames.join(', ')}</div>
            {ingress.hostnames.length > 1 && (
              <div style={{ fontSize: '12px', color: 'var(--tma-text-muted)', marginTop: '2px' }}>
                These hostnames share one certificate, so they always use the same issuer.
              </div>
            )}
          </div>
          <div style={{ flex: 2, minWidth: '220px' }}>
            <Autocomplete
              placeholder={`Default (${data.defaultIssuer})`}
              value={draft[ingress.key] ?? DEFAULT_VALUE}
              isDisabled={!canEdit || !!blockedReason || isSaving}
              onChange={(e: { value: string }) => setDraft((prev) => ({ ...prev, [ingress.key]: e.value }))}
              options={options}
              isSearchable
              filterOption={matchesIssuer}
              noOptionsMessage='No issuer matches'
            />
            {(() => {
              const description = issuers.find((i) => i.name === draft[ingress.key])?.description
              return (
                <div style={{ fontSize: '12px', color: 'var(--tma-text-muted)', marginTop: '4px' }}>
                  {description || (canEdit && options.length > 2 ? 'Type to search by name or description.' : '')}
                </div>
              )
            })()}
          </div>
        </div>
      ))}
      {canEdit && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Button2
            text={isSaving ? 'Saving…' : 'Save'}
            variant='contained'
            isDisabled={!isDirty || isSaving || !!blockedReason}
            buttonSize='32px'
            buttonRadius='8px'
            type='button'
            onClick={save}
          />
          {isDirty && !isSaving && (
            <>
              <Button2 text='Discard' variant='subtle' type='button' onClick={() => data && applyData(data)} />
              <Text textStyle='description' textColor='grey' content='Not live until saved.' />
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default TenantCertificates
